import type { BudgetLine, ExpenseCategory } from "@torre/expenses";
import { budgetLinesFor } from "./budget";
import type { BudgetPeriodAnalysis } from "./budgetPeriod";
import { WARN_AT, scopePeriods, type BudgetTimeline } from "./budgetTimeline";
import type { ProjectedFlow } from "./projectedFlow";

// ---------------------------------------------------------------------------
// Resumen ejecutivo de Presupuestos (#1332). Convierte los números del periodo
// en lo que un consultor pondría en la primera lámina: un veredicto en una
// frase, los hallazgos que lo explican (cada uno con su cifra) y qué hacer.
//
// No calcula nada nuevo: lee la línea de tiempo (plan, real y proyección), el
// análisis mes a mes (pronóstico y su certeza) y el flujo presupuestado (ingreso
// esperado contra gasto). Devuelve códigos y cifras; el texto lo pone la
// interfaz en el idioma de quien lee.
// ---------------------------------------------------------------------------

export type InsightTone = "good" | "warn" | "bad" | "info";

export interface Insight {
  code: string;
  tone: InsightTone;
  /** Cifras del mensaje. `category` es una categoría; `month`, un mes YYYY-MM; lo demás, números. */
  params: Record<string, string | number>;
}

export interface BudgetInsights {
  /** Dónde cae el periodo respecto de hoy. */
  window: "past" | "open" | "future";
  verdict: Insight;
  findings: Insight[];
  actions: Insight[];
}

const MAX_FINDINGS = 6;
const MAX_ACTIONS = 4;
/** Una categoría «domina» el plan desde esta parte del presupuesto. */
const CONCENTRATION_AT = 50;
/** Por debajo de esta certeza, el pronóstico se avisa como poco confiable. */
const LOW_CERTAINTY = 45;
/** Un presupuesto «casi sin usar»: menos de esto ejecutado con más de la mitad del periodo transcurrido. */
const UNDERUSED_PCT = 50;
const round = (n: number) => Math.round(n * 100) / 100;
const pctOf = (part: number, whole: number) => (whole > 0 ? Math.round((part / whole) * 100) : 0);

export function budgetInsights(input: { timeline: BudgetTimeline; analysis?: BudgetPeriodAnalysis | null; flow?: ProjectedFlow | null }): BudgetInsights {
  const { timeline: tl, analysis, flow } = input;
  const months = tl.months.length;
  const window: BudgetInsights["window"] = tl.currentIndex === 0 ? "past" : tl.currentIndex > months ? "future" : "open";
  const hasBudget = tl.totalBudget > 0;
  const findings: Insight[] = [];
  const actions: Insight[] = [];

  // --- Veredicto ------------------------------------------------------------
  let verdict: Insight;
  if (!hasBudget) {
    verdict = { code: "noBudget", tone: "warn", params: { spent: tl.spentYear, months } };
    actions.push({ code: "createBudget", tone: "warn", params: { rate: tl.runRate } });
  } else if (window === "past") {
    const diff = round(tl.totalBudget - tl.spentToDate);
    verdict =
      diff < 0
        ? { code: "pastOver", tone: "bad", params: { spent: tl.spentToDate, budget: tl.totalBudget, amount: -diff, pct: pctOf(tl.spentToDate, tl.totalBudget) } }
        : { code: "pastUnder", tone: "good", params: { spent: tl.spentToDate, budget: tl.totalBudget, amount: diff, pct: pctOf(tl.spentToDate, tl.totalBudget) } };
  } else if (window === "future") {
    // El mismo «cierre esperado» que muestran el indicador y la tabla por categoría.
    const forecast = tl.projectedYearEnd;
    const diff = round(tl.totalBudget - forecast);
    verdict = { code: diff < 0 ? "futureShort" : "futureCovered", tone: diff < 0 ? "warn" : "good", params: { budget: tl.totalBudget, forecast, amount: Math.abs(diff) } };
  } else {
    const diff = round(tl.totalBudget - tl.projectedYearEnd);
    const pct = pctOf(tl.projectedYearEnd, tl.totalBudget);
    if (diff < 0) verdict = { code: "willOverrun", tone: "bad", params: { projected: tl.projectedYearEnd, budget: tl.totalBudget, amount: -diff, pct, ...(tl.overrunMonth ? { month: tl.overrunMonth } : {}) } };
    else if (pct >= WARN_AT) verdict = { code: "tight", tone: "warn", params: { projected: tl.projectedYearEnd, budget: tl.totalBudget, amount: diff, pct } };
    else verdict = { code: "onTrack", tone: "good", params: { projected: tl.projectedYearEnd, budget: tl.totalBudget, amount: diff, pct } };
  }

  // --- Hallazgos por categoría ----------------------------------------------
  const budgeted = tl.categories.filter((c) => c.budget > 0);
  const over = budgeted.filter((c) => c.spent > c.budget).sort((a, b) => b.spent - b.budget - (a.spent - a.budget));
  if (over.length) {
    const top = over[0];
    findings.push({ code: "overCategory", tone: "bad", params: { category: top.category, amount: round(top.spent - top.budget), pct: pctOf(top.spent, top.budget), count: over.length } });
    actions.push({ code: "fixOverrun", tone: "bad", params: { category: top.category, amount: round(top.spent - top.budget) } });
  }
  const risk = budgeted.filter((c) => c.status === "risk").sort((a, b) => b.projected - b.budget - (a.projected - a.budget));
  if (risk.length) {
    const top = risk[0];
    findings.push({ code: "riskCategory", tone: "warn", params: { category: top.category, projected: top.projected, budget: top.budget, amount: round(top.projected - top.budget), count: risk.length } });
    if (!over.length) actions.push({ code: "watchRisk", tone: "warn", params: { category: top.category, amount: round(top.projected - top.budget) } });
  }
  const unbudgeted = tl.categories.filter((c) => c.status === "unbudgeted").sort((a, b) => b.spent - a.spent);
  if (hasBudget && unbudgeted.length) {
    const total = round(unbudgeted.reduce((s, c) => s + c.spent, 0));
    findings.push({ code: "unbudgeted", tone: "warn", params: { category: unbudgeted[0].category, amount: total, count: unbudgeted.length, pct: pctOf(total, tl.spentYear) } });
    // Referencia al mes: su ritmo reciente o, si no lo tiene, lo gastado entre los meses ya transcurridos.
    const elapsedMonths = Math.max(1, tl.months.filter((m) => m.phase !== "future").length);
    actions.push({ code: "budgetUnbudgeted", tone: "warn", params: { category: unbudgeted[0].category, rate: unbudgeted[0].runRate || round(unbudgeted[0].spent / elapsedMonths) } });
  }
  // Solo en un periodo en curso, y cada categoría contra SUS meses con presupuesto:
  // una cuyo plan aún no empieza no está «sin usar».
  if (window === "open") {
    const idle = budgeted
      .filter((c) => c.elapsedPct >= 50 && (c.pct ?? 0) < UNDERUSED_PCT && c.projected < c.budget * (WARN_AT / 100))
      .sort((a, b) => b.budget - b.projected - (a.budget - a.projected));
    if (idle.length) {
      const top = idle[0];
      const free = round(top.budget - top.projected);
      findings.push({ code: "underused", tone: "info", params: { category: top.category, pct: Math.round(top.pct ?? 0), amount: free } });
      if (over.length || risk.length) actions.push({ code: "reallocate", tone: "info", params: { category: top.category, amount: free, to: (over[0] || risk[0]).category } });
    }
  }
  if (budgeted.length > 1) {
    const top = [...budgeted].sort((a, b) => b.budget - a.budget)[0];
    const share = pctOf(top.budget, tl.totalBudget);
    if (share >= CONCENTRATION_AT) findings.push({ code: "concentration", tone: "info", params: { category: top.category, pct: share } });
  }

  // --- ¿Qué tan confiable es lo que falta? ----------------------------------
  // El resumen usa UNA cifra de cierre esperado (la del veredicto y la tabla). El
  // pronóstico mes a mes, que además mira el año pasado, vive en su sección; aquí
  // solo se avisa cuando no hay historia o la certeza es baja.
  const future = analysis?.future;
  if (window !== "past" && future && future.months > 0) {
    if (future.forecast == null) {
      findings.push({ code: "noForecast", tone: "info", params: { months: future.months } });
      actions.push({ code: "loadHistory", tone: "info", params: {} });
    } else if ((future.certainty ?? 0) < LOW_CERTAINTY) {
      findings.push({ code: "lowCertainty", tone: "info", params: { certainty: future.certainty ?? 0, months: analysis?.historyMonths ?? 0 } });
      actions.push({ code: "loadHistory", tone: "info", params: {} });
    }
  }

  // --- ¿El ingreso esperado cubre el gasto? ---------------------------------
  if (flow && flow.remaining.months > 0 && flow.remaining.spend > 0) {
    const r = flow.remaining;
    const coverage = pctOf(r.firm, r.spend);
    if (r.net < 0) {
      findings.push({ code: "flowNegative", tone: "bad", params: { amount: -r.net, coverage, ...(flow.firstNegative ? { month: flow.firstNegative } : {}) } });
      actions.push({ code: "closeGap", tone: "bad", params: { amount: -r.net } });
    } else if (r.netWithoutPipeline < 0) {
      findings.push({ code: "flowDependsOnPipeline", tone: "warn", params: { amount: -r.netWithoutPipeline, pipeline: r.pipeline, coverage } });
      actions.push({ code: "securePipeline", tone: "warn", params: { amount: -r.netWithoutPipeline } });
    } else {
      findings.push({ code: "flowCovered", tone: "good", params: { amount: r.netWithoutPipeline, coverage } });
    }
  }

  const rank: Record<InsightTone, number> = { bad: 0, warn: 1, info: 2, good: 3 };
  const unique = (list: Insight[]) => list.filter((x, i) => list.findIndex((y) => y.code === x.code) === i);
  return {
    window,
    verdict,
    findings: [...findings].sort((a, b) => rank[a.tone] - rank[b.tone]).slice(0, MAX_FINDINGS),
    actions: unique(actions)
      .sort((a, b) => rank[a.tone] - rank[b.tone])
      .slice(0, MAX_ACTIONS),
  };
}

// ---------------------------------------------------------------------------
// Desglose: qué tan completo y equilibrado está el plan del periodo.
// ---------------------------------------------------------------------------

export interface BudgetPlanInsights {
  months: number;
  /** Presupuesto del periodo (líneas mensuales y anuales prorrateadas). */
  total: number;
  monthlyAverage: number;
  monthsCovered: number;
  /** Meses del periodo sin ningún presupuesto. */
  gaps: string[];
  /** Líneas mensuales del periodo que el ajuste semanal puede mover, y las fijadas. */
  auto: number;
  pinned: number;
  /** Líneas anuales que aplican al periodo (se prorratean a 1/12 por mes). */
  yearly: number;
  byCategory: { category: ExpenseCategory; amount: number; pct: number }[];
  byMonth: { period: string; amount: number }[];
  verdict: Insight;
  findings: Insight[];
  actions: Insight[];
}

/** Variación entre el mes más alto y el más bajo a partir de la cual el plan se avisa como disparejo. */
const UNEVEN_PCT = 25;

export function budgetPlanInsights(lines: readonly BudgetLine[], range: { from: string; to: string }, today: string): BudgetPlanInsights {
  const periods = scopePeriods(range);
  const nowPeriod = today.slice(0, 7);
  const byCat = new Map<ExpenseCategory, number>();
  let exact = 0;
  const byMonth = periods.map((period) => {
    const monthLines = budgetLinesFor(lines, period);
    for (const l of monthLines) byCat.set(l.category, (byCat.get(l.category) || 0) + l.amount);
    const amount = monthLines.reduce((s, l) => s + l.amount, 0);
    exact += amount;
    return { period, amount: round(amount) };
  });
  // Se redondea la suma, no cada mes: una anual de 100,000 suma 100,000.
  const total = round(exact);
  const covered = byMonth.filter((m) => m.amount > 0);
  const gaps = byMonth.filter((m) => m.amount <= 0).map((m) => m.period);
  const monthly = lines.filter((l) => l.period.length === 7 && l.period >= range.from && l.period <= range.to);
  const years = new Set(periods.map((p) => p.slice(0, 4)));
  const yearly = lines.filter((l) => l.period.length === 4 && years.has(l.period)).length;
  const pinned = monthly.filter((l) => l.autoAdjust === false).length;
  const categories = [...byCat.entries()].map(([category, amount]) => ({ category, amount: round(amount), pct: pctOf(amount, total) })).sort((a, b) => b.amount - a.amount);

  const findings: Insight[] = [];
  const actions: Insight[] = [];
  let verdict: Insight;
  if (total <= 0) {
    verdict = { code: "planEmpty", tone: "warn", params: { months: periods.length } };
    actions.push({ code: "planCreate", tone: "warn", params: {} });
  } else if (gaps.length) {
    verdict = { code: "planGaps", tone: "warn", params: { total, covered: covered.length, months: periods.length, count: gaps.length } };
  } else {
    verdict = { code: "planComplete", tone: "good", params: { total, months: periods.length, average: round(total / periods.length) } };
  }

  if (total > 0) {
    // Los huecos que importan son los que vienen: un mes pasado sin plan ya no se arregla.
    const ahead = gaps.filter((p) => p >= nowPeriod);
    if (ahead.length) {
      findings.push({ code: "gapsAhead", tone: "warn", params: { count: ahead.length, month: ahead[0] } });
      actions.push({ code: "fillGaps", tone: "warn", params: { count: ahead.length, month: ahead[0] } });
    } else if (gaps.length) {
      findings.push({ code: "gapsPast", tone: "info", params: { count: gaps.length, month: gaps[0] } });
    }
    if (categories.length) {
      const top = categories[0];
      findings.push({ code: top.pct >= CONCENTRATION_AT ? "planConcentrated" : "planTop", tone: "info", params: { category: top.category, pct: top.pct, amount: top.amount, count: categories.length } });
    }
    if (covered.length > 1) {
      const max = covered.reduce((a, b) => (b.amount > a.amount ? b : a));
      const min = covered.reduce((a, b) => (b.amount < a.amount ? b : a));
      const spread = pctOf(max.amount - min.amount, max.amount);
      if (spread >= UNEVEN_PCT) findings.push({ code: "planUneven", tone: "info", params: { month: max.period, amount: max.amount, monthLow: min.period, low: min.amount, pct: spread } });
    }
    if (monthly.length) {
      if (pinned === monthly.length) {
        findings.push({ code: "allPinned", tone: "info", params: { count: pinned } });
      } else if (pinned === 0) {
        findings.push({ code: "allAuto", tone: "info", params: { count: monthly.length } });
        actions.push({ code: "pinWhatMatters", tone: "info", params: {} });
      } else {
        findings.push({ code: "mixedPinned", tone: "info", params: { count: pinned, lines: monthly.length } });
      }
    }
    if (yearly > 0) findings.push({ code: "yearlyLines", tone: "info", params: { count: yearly } });
  }

  const rank: Record<InsightTone, number> = { bad: 0, warn: 1, info: 2, good: 3 };
  return {
    months: periods.length,
    total,
    monthlyAverage: covered.length ? round(total / covered.length) : 0,
    monthsCovered: covered.length,
    gaps,
    auto: monthly.length - pinned,
    pinned,
    yearly,
    byCategory: categories,
    byMonth,
    verdict,
    findings: findings.sort((a, b) => rank[a.tone] - rank[b.tone]).slice(0, MAX_FINDINGS),
    actions: actions.slice(0, MAX_ACTIONS),
  };
}
